import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Box } from '@mantine/core';
import Header from './Header';
import Footer from './Footer';
import { getActiveSeason } from '../../config/seasons';

// Search engines' event card for the homepage, from the active season. Left
// out until the season has an event date, rather than advertising a stale one.
function eventStructuredData() {
  const season = getActiveSeason();
  if (!season?.eventDate) return null;
  const day = season.eventDate.slice(0, 10);
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: `Prix du Canada Ballet Competition ${season.year}`,
    description: 'Premier ballet competition in Toronto and Canada, welcoming dancers of all ages including adult and recreational participants. Every dancer performs on stage.',
    startDate: (season.stageStartsAt || season.eventDate).slice(0, 19),
    endDate: day,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: season.venue || 'Toronto',
      address: { '@type': 'PostalAddress', addressLocality: 'Toronto', addressRegion: 'Ontario', addressCountry: 'Canada' },
    },
    organizer: { '@type': 'Organization', name: 'Prix du Canada', url: 'https://prixducanada.com', email: 'info@prixducanada.com' },
    keywords: 'ballet competition Canada, ballet competition Toronto, adult ballet competition, recreational ballet competition',
  };
  if (season.prices) {
    // "From" price: registration fee + one solo (cart seasons).
    data.offers = {
      '@type': 'Offer',
      price: String(season.prices.registration + season.prices.solo),
      priceCurrency: 'CAD',
      availability: 'https://schema.org/InStock',
      validFrom: season.opensAt.slice(0, 10),
    };
  }
  return JSON.stringify(data);
}

const Layout = ({ children, title, description, keywords }) => {
  // Add Mailchimp script to head
  useEffect(() => {
    const script = document.createElement('script');
    script.id = 'mcjs';
    script.innerHTML = `!function(c,h,i,m,p){m=c.createElement(h),p=c.getElementsByTagName(h)[0],m.async=1,m.src=i,p.parentNode.insertBefore(m,p)}(document,"script","https://chimpstatic.com/mcjs-connected/js/users/1da5efc4af32ed035467f1d53/b9443a002d53c0d7c2c2487dd.js");`;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('mcjs');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const defaultTitle = "Prix du Canada | Premier Ballet Competition in Canada";
  const defaultDescription = "Prix du Canada - Premier ballet competition in Toronto and Canada. Open to all ages including adult and recreational dancers. Every participant performs on stage. Register for Canada's most inclusive ballet competition.";
  const defaultKeywords = "ballet competition Canada, ballet competition Toronto, adult ballet competition, recreational ballet competition Canada, ballet competition Ontario, dance competition Canada, classical ballet competition, inclusive ballet competition";

  return (
    <>
      <Helmet>
        <title>{title || defaultTitle}</title>
        <meta name="description" content={description || defaultDescription} />
        <meta name="keywords" content={keywords || defaultKeywords} />
        <meta name="author" content="Prix du Canada" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://prixducanada.com/" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://prixducanada.com/" />
        <meta property="og:title" content={title || "Prix du Canada | Premier Ballet Competition in Toronto & Canada"} />
        <meta property="og:description" content={description || "Canada's most inclusive ballet competition in Toronto. Open to all ages including adult and recreational dancers. Every participant performs on stage."} />
        <meta property="og:image" content="https://prixducanada.com/images/logo_main.png" />
        <meta property="og:site_name" content="Prix du Canada" />
        <meta property="og:locale" content="en_CA" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://prixducanada.com/" />
        <meta property="twitter:title" content={title || "Prix du Canada | Premier Ballet Competition in Toronto & Canada"} />
        <meta property="twitter:description" content={description || "Canada's most inclusive ballet competition in Toronto. Open to all ages including adult and recreational dancers."} />
        <meta property="twitter:image" content="https://prixducanada.com/images/logo_main.png" />

        {/* Structured Data - only on the homepage, from the active season */}
        {!title && eventStructuredData() && (
          <script type="application/ld+json">{eventStructuredData()}</script>
        )}
      </Helmet>

      <Header />
      <Box component="main">
        {children}
      </Box>
      <Footer />
    </>
  );
};

export default Layout;
